/**
 * Whether this workspace can send, in the header, because it is the third
 * question and its card is six cards down.
 *
 * ## WHY IT IS HERE AND NOT SOMEWHERE SENSIBLE-LOOKING
 *
 * Slice 25's done-when 2 asks for three things to be visible at 1,280 × 800
 * without scrolling: the credit balance, whether the workspace can send, and
 * the most recent campaign. Two of those were solved by moving a card — credits
 * became a chip, recent campaigns moved up seven places. This one cannot be,
 * and `HomePage`'s own header already conceded it years of slices ago:
 *
 *   > The one deliberate compromise: "can this workspace send" is the third
 *   > question the brief names as above-the-fold, and its card is not — but its
 *   > *signal* is.
 *
 * That signal was `QuickActions`' primary button, which works only when
 * something is **broken**: a healthy workspace's top tile says "New campaign",
 * which answers what to do next and not whether sending works. And slice 25
 * demoted the sandbox out of that slot precisely because a working-but-limited
 * account was being drawn as a repair. So the compromise now leaks in the other
 * direction too, and the honest fix is a chip that is always there and says
 * which of the states the workspace is in.
 *
 * ## IT IS THE SAME VERDICT AS THE CARD, NOT A SECOND OPINION
 *
 * `SES_READINESS_DISPLAY` and `META_READINESS_DISPLAY` are the tables the
 * sendability card draws its own badges from, so this chip and that card are one
 * verdict rendered twice rather than two verdicts that can disagree. Nothing
 * here derives a state, compares a flag or re-reads a snapshot; it looks the
 * state up. The Overview has shipped that defect once already — two places
 * computing "is this healthy" and drifting — and `AppLayout`'s header makes the
 * same argument about the pillar verdict.
 *
 * ## ONE CHIP PER CONNECTED CHANNEL
 *
 * Not one overall verdict. A workspace with email ready and WhatsApp flagged has
 * no single true answer to "can you send", and picking the worse of the two
 * would tell a sender their email is broken. Slice 24 made WhatsApp-only
 * workspaces real, so the email chip is not assumed either.
 *
 * A channel Wend has never successfully spoken to carries `state: null` and gets
 * no chip: a verdict over a snapshot that was never taken is the defect
 * `QuickActions` documents at length, where `sendingEnabled` and `sandboxed` are
 * defaults rather than observations. With neither channel connected the chip
 * says that instead, which is the one case where a single answer is true.
 *
 * Every chip links to the sendability card under its anchor, where the verdict
 * has its Callout, its repair and its age. The chip is the answer; the card is
 * the reason.
 */
import type { OverviewResponse } from '@wend/shared';
import { cn, focusRing, type BadgeTone } from '@wend/ui';

import { SES_READINESS_DISPLAY } from '../../lib/integration-display';
import { META_READINESS_DISPLAY } from '../../lib/whatsapp-display';
import { integrationTabPath } from '../integrations/integration-tab-url';

interface ChannelVerdict {
  readonly key: string;
  readonly label: string;
  readonly tone: BadgeTone;
}

export function sendabilityVerdicts(
  sendability: OverviewResponse['sendability'],
): readonly ChannelVerdict[] {
  const { ses, whatsapp } = sendability;
  const verdicts: ChannelVerdict[] = [];

  if (ses.connected && ses.state !== null) {
    const badge = SES_READINESS_DISPLAY[ses.state].badge;
    verdicts.push({ key: 'email', label: `Email: ${badge.label}`, tone: badge.tone });
  }

  if (whatsapp.connected && whatsapp.state !== null) {
    const display = META_READINESS_DISPLAY[whatsapp.state];
    verdicts.push({ key: 'whatsapp', label: `WhatsApp: ${display.label}`, tone: display.tone });
  }

  return verdicts;
}

/**
 * The header pill, which is deliberately NOT a `Badge`.
 *
 * `Badge` is this page's semantic chip: tinted border, tinted background,
 * uppercase mono, and the tone colouring the text itself. That vocabulary
 * belongs to the figures below — `warn` there means "Amazon may review your
 * account" — and the owner's judgement on the header was that its chips did not
 * match the approved design, which uses a **neutral** pill with a 7px coloured
 * dot: the shape stays constant and only the dot carries the verdict.
 *
 * The words stay the product's. The mockup's label is "Email connected", which
 * collapses sandboxed, ready and paused into one phrase and leaves the dot as
 * the only carrier of a difference that decides whether mail goes out. These
 * keep `SES_READINESS_DISPLAY`'s own verdicts, so the text says it too.
 */
const CHIP_DOT: Readonly<Record<BadgeTone, string>> = {
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  info: 'bg-info',
  neutral: 'bg-muted',
  // Slice 65's `brand` tone is identity, not a verdict; no sendability verdict
  // uses it, but a total map has to say what its dot would be.
  brand: 'bg-brand-b',
  // Slice 66's identity tones name a kind, not a verdict; none is a
  // sendability state, and each draws its own identity colour if one were.
  'ident-a': 'bg-ident-a',
  'ident-b': 'bg-ident-b',
  'ident-c': 'bg-ident-c',
  'ident-d': 'bg-ident-d',
};

function Chip({ tone, label }: { tone: BadgeTone; label: string }) {
  return (
    <span
      data-role="sendability-chip"
      className={cn(
        'inline-flex items-center gap-[7px] rounded-full border border-line-strong',
        'bg-white/[0.04] px-3 py-1 text-[0.78rem] leading-5 text-muted',
      )}
    >
      <span aria-hidden="true" className={cn('size-[7px] shrink-0 rounded-full', CHIP_DOT[tone])} />
      {label}
    </span>
  );
}

export function SendabilityChip({
  sendability,
}: {
  sendability: OverviewResponse['sendability'];
}) {
  const verdicts = sendabilityVerdicts(sendability);
  const { ses, whatsapp } = sendability;

  /*
   * Neither channel connected is the one state with a single true answer, and
   * it is the state a new workspace is in. A connected channel Wend has not
   * managed to check yet is NOT this — it has a connection and an unknown
   * verdict — so it falls through to no chip rather than being called
   * unconnected.
   */
  const nothingConnected = !ses.connected && !whatsapp.connected;
  if (verdicts.length === 0 && !nothingConnected) return null;

  return (
    <a
      href={integrationTabPath('email')}
      aria-label="Whether this workspace can send. Opens the sendability card."
      className={cn('inline-flex items-center gap-2 rounded-full', focusRing)}
    >
      {nothingConnected ? (
        <Chip tone="warn" label="Nothing connected" />
      ) : (
        verdicts.map((verdict) => (
          <Chip key={verdict.key} tone={verdict.tone} label={verdict.label} />
        ))
      )}
    </a>
  );
}
